import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const rad = (deg) => (deg * Math.PI) / 180;

// Build a radial graph in viewport-percentage coordinates from the
// interpreted structure: core -> domains -> branches.
function useGraph(interpretation) {
  return useMemo(() => {
    const nodes = [];
    const links = [];
    if (!interpretation || !interpretation.core) return { nodes, links };

    const core = { id: "core", label: interpretation.core.label, level: 0, x: 50, y: 21 };
    nodes.push(core);

    const domains = interpretation.domains || [];
    const n = Math.max(domains.length, 1);
    domains.forEach((d, i) => {
      const angle = -90 + (360 / n) * i + 34;
      const dx = 31 * Math.cos(rad(angle));
      const dy = 30 * Math.sin(rad(angle));
      const dnode = {
        id: `d${i}`,
        label: d.label,
        level: 1,
        x: clamp(50 + dx, 7, 93),
        y: clamp(50 + dy, 13, 87),
      };
      nodes.push(dnode);
      links.push({ id: `lc${i}`, a: core, b: dnode });

      const out = Math.atan2(dnode.y - 50, dnode.x - 50) * (180 / Math.PI);
      const m = d.branches.length;
      d.branches.forEach((b, j) => {
        const spread = m > 1 ? -52 + (104 / (m - 1)) * j : 0;
        const a = out + spread;
        const bx = dnode.x + 13 * Math.cos(rad(a));
        const by = dnode.y + 13.5 * Math.sin(rad(a));
        const bnode = {
          id: `d${i}b${j}`,
          label: b.label,
          level: 2,
          x: clamp(bx, 3, 97),
          y: clamp(by, 6, 94),
        };
        nodes.push(bnode);
        links.push({ id: `ld${i}b${j}`, a: dnode, b: bnode });
      });
    });

    return { nodes, links };
  }, [interpretation]);
}

function curve(a, b) {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const nx = -(b.y - a.y);
  const ny = b.x - a.x;
  const len = Math.hypot(nx, ny) || 1;
  const bend = 4;
  const cx = mx + (nx / len) * bend;
  const cy = my + (ny / len) * bend;
  return `M ${a.x} ${a.y} Q ${cx} ${cy} ${b.x} ${b.y}`;
}

const LABEL = {
  0: "font-display text-lg md:text-2xl tracking-tight",
  1: "font-display text-base md:text-lg tracking-tight",
  2: "text-[11px] md:text-xs tracking-wide",
};
const COLOR = { 0: "#525252", 1: "#8C8C8C", 2: "#BDBDBD" };

export function NeuralMapLayer({ interpretation }) {
  const { nodes, links } = useGraph(interpretation);

  return (
    <div className="pointer-events-none absolute inset-0 z-0 hidden md:block" aria-hidden data-testid="neural-map-layer">
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        <AnimatePresence>
          {links.map((l) => (
            <motion.path
              key={l.id}
              d={curve(l.a, l.b)}
              fill="none"
              stroke="#C8C8C4"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.5 }}
              exit={{ opacity: 0, transition: { duration: 0.8 } }}
              transition={{ pathLength: { duration: 1.6, ease: [0.16, 1, 0.3, 1] }, opacity: { duration: 1 } }}
            />
          ))}
        </AnimatePresence>
        <AnimatePresence>
          {nodes.map((nd) => (
            <motion.circle
              key={`dot-${nd.id}`}
              cx={nd.x}
              cy={nd.y}
              r={nd.level === 0 ? 0.55 : nd.level === 1 ? 0.42 : 0.3}
              fill={COLOR[nd.level]}
              vectorEffect="non-scaling-stroke"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.7, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7 }}
            />
          ))}
        </AnimatePresence>
      </svg>

      <AnimatePresence>
        {nodes.map((nd, i) => (
          <motion.div
            key={`lbl-${nd.id}`}
            className="absolute"
            style={{ left: `${nd.x}%`, top: `${nd.y}%`, transform: "translate(-50%, -50%)" }}
            initial={{ opacity: 0, filter: "blur(3px)" }}
            animate={{ opacity: 1, filter: "blur(0px)", y: [0, nd.level === 2 ? -4 : -3, 0] }}
            exit={{ opacity: 0, filter: "blur(3px)", transition: { duration: 0.8 } }}
            transition={{
              opacity: { duration: 1.1, delay: nd.level * 0.15 + (i % 5) * 0.05 },
              filter: { duration: 1.1 },
              y: { duration: 9 + (i % 5), repeat: Infinity, ease: "easeInOut" },
            }}
          >
            <span className={LABEL[nd.level]} style={{ color: COLOR[nd.level], whiteSpace: "nowrap" }}>
              {nd.label}
            </span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
